import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { makeStyles } from "@mui/styles";
import Options from "../components/Options";
import ModalAbout from "../components/ModalAbout";
import { useDispatch, useSelector } from "react-redux";
import { setProvinceId } from "../redux/ProvinceSlice";
import {
  setDistrictId,
  setProvDistrict,
} from "../redux/DistrictSlice";
import { setLanguageId } from "../redux/LanguageSlice";
import { resetOffStates } from "../redux/OffenceSlice";
import { resetOrgStates } from "../redux/OrganizationSlice";
import {
  getDistrictListLabel,
  getTitleFromAppContent,
} from "../helpers/utils";
import { trackPageView } from "../helpers/api";
import PageBackButton from "../components/PageBackButton";
import { changeTab } from "../redux/TabSlice";
import { toast } from "react-toastify";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  bgcolor: {
    backgroundColor: "#FFFFFF",
  },
  textColor: {
    color: theme.palette.primary.main,
  },

  sectionbg: {
    background: theme.palette.primary.main,
  },
  sectionOrange: {
    background: "#D88284",
  },
  sectionBgLight: {
    backgroundColor: "#F6F1F4",
  },
}));

const Settings = () => {
  const classes = useStyles();
  const { language, selLanguageId, isEnglish } = useSelector(
    (state) => state.language
  );
  const { district, selDistrictId } = useSelector(
    (state) => state.district
  );
  const { province, federalId, selProvinceId } = useSelector(
    (state) => state.province
  );

  useEffect(() => {
    trackPageView("Settings", { province: selProvinceId, district: selDistrictId, language: selLanguageId });
  }, [selProvinceId, selDistrictId, selLanguageId]);
  const { appContent } = useSelector((state) => state.appContent);
  const { backFromSettings } = useSelector((state) => state.page);
  const dispatch = useDispatch();
  const [openAbout, setOpenAbout] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openDis, setOpenDis] = useState(false);
  const [lan, setLan] = useState();
  const [settingsTitle, setSettingsTitle] = useState("");
  const [languageTitle, setLanguageTitle] = useState("");
  const [chLocTitle, setChLocTitle] = useState("");
  const [chDisTitle, setChDisTitle] = useState("");
  const [districtSearch, setDistrictSearch] = useState("");
  const [tabSelect, setTabSelect] = useState("Settings");
  const handleOpen = () => setOpenAbout(true);
  const handleClose = () => {
    setOpenAbout(false);
  };

  useEffect(() => {
    if (appContent) {
      setSettingsTitle(getTitleFromAppContent(appContent, "Settings"));
      setLanguageTitle(getTitleFromAppContent(appContent, "Language"));
      setChLocTitle(getTitleFromAppContent(appContent, "Change Location"));
      setChDisTitle(getTitleFromAppContent(appContent, "Choose your District"));
    }
  }, [appContent]);

  useEffect(() => {
    if (!openDis) setDistrictSearch("");
  }, [openDis]);

  const districtSearchPlaceholder = useMemo(() => {
    const fromCms = getTitleFromAppContent(
      appContent,
      "Search districts",
      selLanguageId
    );
    if (fromCms) return fromCms;
    return isEnglish ? "Search districts…" : "ضلاع تلاش کریں…";
  }, [appContent, selLanguageId, isEnglish]);

  const filteredDistricts = useMemo(() => {
    if (!districtSearch.trim()) return district;
    const q = districtSearch.trim().toLowerCase();
    return district.filter((item) =>
      getDistrictListLabel(item).toLowerCase().includes(q)
    );
  }, [district, districtSearch]);

  useEffect(() => {
    dispatch(changeTab(tabSelect));
  }, [tabSelect, dispatch]);

  useEffect(() => {
    if (!language?.length) return;
    const match = language.find((lang) => lang._id === selLanguageId);
    setLan(match?.language ?? "");
  }, [selLanguageId, language]);

  useEffect(() => {
    dispatch(resetOffStates());
  }, [dispatch]);

  const handleChangeLang = (id) => {
    dispatch(setLanguageId(id));
    setOpenDropdown(false);
    dispatch(resetOffStates());
    dispatch(resetOrgStates());
  };

  const handleChangeProv = (item) => {
    setOpenDis(true);
    dispatch(setProvDistrict(item.districts));
    dispatch(setProvinceId(item._id));
    dispatch(setDistrictId(""));
  };

  const handleBack = () => {
    if (!selDistrictId) {
      toast.error("Select District");
      return;
    }
    if (backFromSettings === "Settings") setTabSelect("HumQadam");
    else if (
      backFromSettings === "Helpline Details" ||
      backFromSettings === "Provincial Helplines" ||
      backFromSettings === "National Helplines" ||
      backFromSettings === "Service Providers" ||
      backFromSettings === "HelplineDetails"
    )
      setTabSelect("ReferralDirectory");
    else if (backFromSettings === "GuideStep")
      setTabSelect("StepGuide");
    else if (backFromSettings === "RelevantLaws")
      setTabSelect("Laws");
    else setTabSelect(backFromSettings);
  };

  const locationButtonClass = (selected) =>
    clsx(
      "w-full p-3 px-5 text-white flex items-center rounded-xl min-h-[44px] touch-manipulation",
      isEnglish ? "text-left flex-row" : "text-right flex-row-reverse",
      selected ? classes.sectionOrange : classes.sectionbg
    );

  return (
    <div
      onClick={() => setOpenDropdown(false)}
      className={`${classes.sectionBgLight} min-h-[100dvh] flex flex-col`}
    >
      <Header />

      <div className="flex items-start justify-between gap-2 w-full">
        <PageBackButton onClick={handleBack} />
        <Options handleOpen={handleOpen} hideSettings />
      </div>

      <div className="px-5 mt-2 pb-10 hq-safe-bottom w-full max-w-md mx-auto">
        <h6
          className={`text-center mb-8 text-2xl ${classes.textColor} font-bold`}
        >
          {settingsTitle}
        </h6>

        <h6 className={`text-center ${classes.textColor} font-bold`}>
          {languageTitle}
        </h6>
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative mt-3 w-full"
        >
          <button
            type="button"
            onClick={() => setOpenDropdown(!openDropdown)}
            aria-expanded={openDropdown}
            aria-haspopup="listbox"
            className={`w-full px-5 py-3 min-h-[44px] cursor-pointer rounded-3xl text-left ${classes.textColor} text-sm ${classes.bgcolor} border-0 touch-manipulation`}
          >
            {lan}
          </button>
          {openDropdown && (
            <ul
              role="listbox"
              className={`absolute left-0 right-0 top-full z-20 bg-white p-4 mt-1 rounded-xl shadow-md border border-gray-100 ${classes.textColor} text-sm list-none m-0`}
            >
              {language.map((langItem) => (
                <li
                  key={langItem._id}
                  role="option"
                  aria-selected={langItem._id === selLanguageId}
                  onClick={() => handleChangeLang(langItem._id)}
                  className="pb-2 last:pb-0 cursor-pointer min-h-[44px] flex items-center touch-manipulation"
                >
                  {langItem.language}
                </li>
              ))}
            </ul>
          )}
        </div>

        <h6
          className={`text-center my-3 mt-6 ${classes.textColor} font-bold`}
        >
          {chLocTitle}
        </h6>
        <div className="flex flex-col gap-2 w-full">
          {province.map((item) =>
            item._id !== federalId ? (
              <button
                key={item._id}
                type="button"
                className={locationButtonClass(selProvinceId === item._id)}
                onClick={() => handleChangeProv(item)}
              >
                {item.icon && (
                  <img
                    className="w-7 h-7 mx-3 shrink-0"
                    alt=""
                    src={item.icon}
                  />
                )}
                <span>{item.name?.text || item.name}</span>
              </button>
            ) : null
          )}
        </div>

        {openDis && (
          <>
            <h6
              className={`text-center my-3 mt-6 ${classes.textColor} font-bold`}
            >
              {chDisTitle}
            </h6>
            <input
              type="search"
              value={districtSearch}
              onChange={(e) => setDistrictSearch(e.target.value)}
              placeholder={districtSearchPlaceholder}
              aria-label={districtSearchPlaceholder}
              className={`w-full mb-2 px-4 py-3 min-h-[44px] rounded-xl border border-gray-200 bg-white text-sm ${classes.textColor} outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-[#A94E76]`}
            />
            {districtSearch.trim() && filteredDistricts.length === 0 ? (
              <p
                className={`text-center text-sm py-3 px-2 ${classes.textColor}`}
              >
                {isEnglish
                  ? "No districts match your search."
                  : "آپ کی تلاش سے کوئی ضلع مطابقت نہیں رکھتا۔"}
              </p>
            ) : null}
            <div className="max-h-[min(55vh,28rem)] overflow-y-auto overscroll-y-contain w-full -mx-1 px-1">
              <div className="flex flex-col gap-2">
                {filteredDistricts.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className={locationButtonClass(selDistrictId === item._id)}
                    onClick={() => dispatch(setDistrictId(item._id))}
                  >
                    {item.icon && (
                      <img
                        className="w-7 h-7 mx-3 shrink-0"
                        alt=""
                        src={item.icon}
                      />
                    )}
                    <span>{getDistrictListLabel(item)}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <ModalAbout open={openAbout} handleClose={handleClose} />
    </div>
  );
};

export default Settings;
